import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiShoppingBag, FiArrowLeft } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersAPI, paymentsAPI } from '../services/api';
import { toast } from 'react-toastify';

const CartPage = () => {
  const { cartItems, updateQuantity, removeFromCart, clearCart, getCartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentType, setPaymentType] = useState('PAY_LATER');
  const [loading, setLoading] = useState(false);
  const [failedImages, setFailedImages] = useState({});

  const [formData, setFormData] = useState({
    phone: user?.phone || '',
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || 'Gujarat',
    pincode: user?.address?.pincode || '',
    notes: '',
  });

  const total = getCartTotal();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async () => {
    if (!formData.phone || !formData.street || !formData.city || !formData.pincode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderData = {
        items: cartItems.map((item) => ({
          product: item.productId,
          productName: item.productName,
          pricePerKg: item.pricePerKg,
          quantityKg: item.quantityKg,
          itemTotal: item.itemTotal,
        })),
        shippingAddress: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
        },
        phone: formData.phone,
        notes: formData.notes,
        paymentMethod: paymentType === 'PAY_NOW' ? 'ONLINE' : 'PAY_LATER',
      };

      const { data } = await ordersAPI.create(orderData);

      if (!data.success) {
        toast.error(data.message || 'Failed to place order');
        return;
      }

      const order = data.order;

      // PAY_LATER — order placed, no payment now
      if (paymentType === 'PAY_LATER') {
        toast.success('Order placed! Complete payment before dispatch.');
        clearCart();
        navigate(`/orders/${order._id}`);
        return;
      }

      // PAY_NOW — initiate Razorpay
      try {
        const paymentRes = await paymentsAPI.createOrder({ orderId: order._id });

        if (paymentRes.data.success) {
          const { razorpayOrder, key } = paymentRes.data;

          const options = {
            key,
            amount: razorpayOrder.amount,
            currency: razorpayOrder.currency,
            name: 'Swaad Pickles',
            description: `Order ${order.orderNumber}`,
            order_id: razorpayOrder.id,
            handler: async (response) => {
              try {
                await paymentsAPI.verify(response);
                toast.success('Payment successful! 🎉');
              } catch {
                toast.warning('Payment verification pending. We\'ll update you soon.');
              }
              clearCart();
              navigate(`/orders/${order._id}`);
            },
            prefill: {
              name: user?.name,
              email: user?.email,
              contact: formData.phone,
            },
            theme: {
              color: '#D4421E',
            },
            modal: {
              ondismiss: () => {
                toast.info('Payment dismissed. Your order is placed — complete payment before dispatch.');
                clearCart();
                navigate(`/orders/${order._id}`);
              },
            },
          };

          if (window.Razorpay) {
            const rzp = new window.Razorpay(options);
            rzp.open();
          } else {
            toast.info('Payment gateway unavailable. Order placed — pay from orders page.');
            clearCart();
            navigate(`/orders/${order._id}`);
          }
        }
      } catch (paymentError) {
        const errMsg = paymentError.response?.data?.message || 'Payment gateway error';
        toast.error(`Order placed but payment failed: ${errMsg}. You can retry payment from your orders page.`);
        clearCart();
        navigate(`/orders/${order._id}`);
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to place order';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="page container">
        <div className="empty-state">
          <div className="emoji">🛒</div>
          <h3>Your cart is empty</h3>
          <p>Browse our pickle collection and add your favorites!</p>
          <button className="btn btn-primary" style={{ width: 'auto', marginTop: 20 }} onClick={() => navigate('/')}>
            <FiArrowLeft /> Browse Pickles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page container cart-page">
      <h2 style={{ marginBottom: 24 }}>🛒 Your Cart</h2>

      {/* Cart Items */}
      {cartItems.map((item) => (
        <div key={item.productId} className="cart-item">
          <div
            style={{
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #FFF3E0, #FFDAB9)',
              borderRadius: 'var(--radius-sm)',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {item.image && !failedImages[item.productId] ? (
              <img
                src={item.image}
                alt={item.productName}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={() => setFailedImages((prev) => ({ ...prev, [item.productId]: true }))}
              />
            ) : (
              <span style={{ fontSize: '2.2rem', lineHeight: 1 }}>🫙</span>
            )}
          </div>
          <div className="cart-item-info">
            <div className="cart-item-name">{item.productName}</div>
            <div className="cart-item-details">
              {item.quantityKg} kg × ₹{item.pricePerKg}/kg
            </div>
            <div className="quantity-selector" style={{ marginTop: 8, marginBottom: 0 }}>
              <div className="qty-controls">
                <button className="qty-btn" onClick={() => updateQuantity(item.productId, item.quantityKg - 0.5)}>−</button>
                <input className="qty-value" value={item.quantityKg} readOnly />
                <button className="qty-btn" onClick={() => updateQuantity(item.productId, item.quantityKg + 0.5)}>+</button>
              </div>
            </div>
          </div>
          <div className="cart-item-total">₹{item.itemTotal}</div>
          <button className="cart-item-remove" onClick={() => removeFromCart(item.productId)}>
            <FiTrash2 />
          </button>
        </div>
      ))}

      {/* Cart Summary */}
      <div className="cart-summary">
        <h3>Order Summary</h3>
        {cartItems.map((item) => (
          <div key={item.productId} className="cart-total-row">
            <span>{item.productName} ({item.quantityKg} kg)</span>
            <span>₹{item.itemTotal}</span>
          </div>
        ))}
        <div className="cart-total-row grand-total">
          <span>Total</span>
          <span>₹{total}</span>
        </div>

        {!showCheckout ? (
          <button
            className="btn btn-primary mt-20"
            onClick={() => setShowCheckout(true)}
          >
            <FiShoppingBag /> Proceed to Checkout
          </button>
        ) : null}
      </div>

      {/* Checkout Form */}
      {showCheckout && (
        <div className="checkout-form mt-20">
          <h3 style={{ marginBottom: 18, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 8 }}>📍 Delivery Details</h3>

          <div className="form-group">
            <label>Phone Number *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+91 XXXXXXXXXX"
            />
          </div>

          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="Flat / House No., Building, Street, Area"
            />
            <p style={{ marginTop: 5, fontSize: '0.76rem', color: '#b45309', background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 6, padding: '5px 10px', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.5 }}>
              <span>📦</span>
              <span><strong>Please provide your complete address including Flat / House No. and Building name.</strong> This address will be used for courier dispatch — an incomplete or invalid address may cause shipping delays or returns.</span>
            </p>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label>Pincode *</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                placeholder="XXXXXX"
              />
            </div>
          </div>

          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Any special instructions..."
            />
          </div>

          {/* Payment Options */}
          <h3 style={{ marginBottom: 14, color: 'var(--text-heading)' }}>💳 Payment Option</h3>

          <div className="payment-options">
            <div
              className={`payment-option ${paymentType === 'PAY_NOW' ? 'selected' : ''}`}
              onClick={() => setPaymentType('PAY_NOW')}
            >
              <h4>💳 Pay Online</h4>
              <p>Pay full amount securely via Razorpay</p>
            </div>
            <div
              className={`payment-option ${paymentType === 'PAY_LATER' ? 'selected' : ''}`}
              onClick={() => setPaymentType('PAY_LATER')}
            >
              <h4>🕐 Pay Later ✅</h4>
              <p>Place order now, pay before dispatch</p>
            </div>
          </div>
          {paymentType === 'PAY_NOW' && (
            <div style={{ marginTop: 10, padding: '12px 16px', background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 'var(--radius-sm)', fontSize: '0.84rem', color: '#1B5E20' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>✅ You will pay the full order amount online.</div>
              <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#2E7D32', fontStyle: 'italic', borderTop: '1px solid #C8E6C9', paddingTop: 6 }}>
                🚚 <strong>Note:</strong> Shipping charges to be paid extra at the time of order dispatch as per the actual shipping amount.
              </div>
            </div>
          )}
          {paymentType === 'PAY_LATER' && (
            <div style={{ marginTop: 10, padding: '12px 16px', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 'var(--radius-sm)', fontSize: '0.84rem', color: '#92400E' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>⚠️ Your order will be placed. Please complete payment before dispatch.</div>
              <div style={{ marginTop: 6, fontSize: '0.78rem', color: '#78350F', fontStyle: 'italic', borderTop: '1px solid #FCD34D', paddingTop: 6 }}>
                🚚 <strong>Note:</strong> Shipping charges to be paid extra at the time of order dispatch as per the actual shipping amount.
              </div>
            </div>
          )}

          <button
            className={`btn btn-success mt-10 ${loading ? 'btn-disabled' : ''}`}
            style={{ width: '100%' }}
            onClick={handlePlaceOrder}
            disabled={loading}
          >
            {loading ? '⏳ Placing Order...' : paymentType === 'PAY_NOW' ? '✅ Place Order & Pay Now' : '✅ Place Order (Pay Later)'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
